import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes, type ElementType, type CSSProperties, type Ref } from 'react';

interface IconButtonBaseProps {
  className?: string;
  style?: CSSProperties;
  as?: ElementType;
  href?: string;
  target?: string;
  download?: boolean | string;
}

export type IconButtonProps = IconButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof IconButtonBaseProps> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof IconButtonBaseProps>;

export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(function IconButton({
  as,
  href,
  target,
  download,
  className,
  children,
  style,
  ...rest
}, ref) {
  const Tag = (as ?? (href ? 'a' : 'button')) as ElementType;

  // Tabnabbing guard: any link opening in a new tab must carry rel="noopener
  // noreferrer" so the opened page can't access window.opener / referrer.
  // Default it here so call sites can't forget.
  const { rel: explicitRel, type: explicitType, ...restWithoutRel } = rest as Record<string, unknown> & {
    rel?: string;
    type?: 'button' | 'submit' | 'reset';
  };
  const rel = explicitRel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  // Default native buttons to type="button". A bare <button> defaults to
  // type="submit" (HTML spec), so an IconButton dropped inside a <form> becomes
  // an accidental submit — and, as the first submit button in the tree, it wins
  // the form's implicit submission. The SignIn password step's back button hit
  // exactly this: pressing Enter fired "back" instead of "Sign In". Explicit
  // type="submit" still works because it overrides this default.
  const buttonType = Tag === 'button' ? (explicitType ?? 'button') : explicitType;

  // F19: an icon-only button has no text content, so screen readers find no
  // accessible name. Warn loudly (dev only) so consumers add aria-label,
  // aria-labelledby, or title.
  if (process.env.NODE_ENV !== 'production') {
    const restRecord = restWithoutRel as Record<string, unknown>;
    const hasAccessibleName =
      typeof restRecord['aria-label'] === 'string' ||
      typeof restRecord['aria-labelledby'] === 'string' ||
      typeof restRecord.title === 'string';
    if (!hasAccessibleName) {
       
      console.warn(
        '[IconButton] icon-only button is missing an accessible name. ' +
          'Pass aria-label, aria-labelledby, or title so screen readers can identify it.',
      );
    }
  }

  return (
    <Tag
      ref={ref as Ref<HTMLElement>}
      type={buttonType}
      href={href}
      target={target}
      rel={rel}
      download={download}
      className={className}
      style={style}
      {...restWithoutRel}
    >
      {children}
    </Tag>
  );
});

IconButton.displayName = 'IconButton';
